// `/max` metadata is required for real validation: the default (min) build
// carries only possible-length data, so `isValid()` silently degrades to a
// length check and accepts non-dialable numbers like +20105232122.
import { parsePhoneNumberFromString } from "libphonenumber-js/max";

/**
 * Plain, framework-free phone helpers built on libphonenumber-js.
 *
 * This is the single implementation of phone parsing/normalization shared by
 * the server domain and the api-client. It returns PLAIN values only — no
 * branded/domain types live here. Callers that need brands (`PhoneE164`,
 * `WhatsAppWaIdDigits`) wrap these functions and apply the brand themselves
 * (the server in its domain value-objects, the api-client in its own ids).
 */

/**
 * Parsed phone info as plain values.
 */
export interface ParsedPhone {
	/** ISO country code when libphonenumber can resolve it. */
	country?: string;
	/** Canonical E.164, e.g. `+12025551234`. */
	e164: string;
	/** WhatsApp digits-only form, e.g. `12025551234`. */
	whatsappDigits: string;
}

/**
 * Strictly parse a phone number using libphonenumber-js.
 * Returns `null` when the input is missing or invalid (never throws).
 */
export function parsePhone(input: string): ParsedPhone | null {
	if (typeof input !== "string") {
		return null;
	}
	const trimmed = input.trim();
	if (!trimmed) {
		return null;
	}

	// Remove all non-digit and non-plus characters.
	const cleaned = trimmed.replace(/[^\d+]/g, "");
	if (!cleaned) {
		return null;
	}

	// Ensure it starts with + for libphonenumber-js parsing if it's just digits.
	const candidate = cleaned.startsWith("+") ? cleaned : `+${cleaned}`;
	const parsed = parsePhoneNumberFromString(candidate);

	if (!parsed?.isValid()) {
		return null;
	}

	return {
		e164: parsed.number,
		whatsappDigits: parsed.number.replace("+", ""),
		country: parsed.country,
	};
}

/**
 * Normalize any phone string to canonical +E.164.
 * Returns `null` when the input is missing or invalid.
 */
export function normalizePhoneToE164(input: string): string | null {
	return parsePhone(input)?.e164 ?? null;
}

/**
 * Predicate for whether a string is a valid E.164 phone number.
 */
export function isValidPhoneE164(value: string | null | undefined): boolean {
	if (!value) {
		return false;
	}
	return parsePhone(value) !== null;
}
